import { z } from "zod";
import { optional, storableText } from "./appointments";
import { recordId } from "./contracts";
import { absentOrBlankAsNull, searchTextUpTo, unset } from "./fields";
import { propertyReadQuery } from "./properties";

export const unit = z.object({
  id: recordId,
  property: recordId,
  number: z.string(),
  floorPlan: z.string().nullable(),
  beds: z.number().int().nonnegative(),
  baths: z.number().int().nonnegative(),
});
export type Unit = z.infer<typeof unit>;

export const UNIT_PAGE_SIZE_MAX = 1000;
export const UNIT_NUMBER_MAX_LENGTH = 200;
export const UNIT_FLOOR_PLAN_MAX_LENGTH = 2048;
export const UNIT_ROOMS_MAX = 1000;
export const MISSING_UNITS_MAX = 100;
export const MISSING_UNITS_BODY_BYTES = 12 * 1024;
export const UNIT_NUMBERS_PER_LIST_MAX = 100;

const exactNumber = z
  .string()
  .min(1)
  .max(UNIT_NUMBER_MAX_LENGTH)
  .refine((value) => !value.includes("\0"), "no NUL characters");

export const unitListQuery = propertyReadQuery.extend({
  search: optional(searchTextUpTo(UNIT_NUMBER_MAX_LENGTH)),
  numbers: optional(z.array(exactNumber).min(1).max(UNIT_NUMBERS_PER_LIST_MAX)),
  order: z.preprocess(unset, z.enum(["asc", "desc"]).default("asc")),
  page: z.preprocess(unset, z.coerce.number().int().min(1).max(10_000).default(1)),
  pageSize: z.preprocess(unset, z.coerce.number().int().min(1).max(UNIT_PAGE_SIZE_MAX).default(25)),
});
export type UnitListQuery = z.infer<typeof unitListQuery>;

export const unitPage = z.object({
  items: z.array(unit),
  total: z.number().int().nonnegative(),
});
export type UnitPage = z.infer<typeof unitPage>;

const rooms = z.number().int().min(0).max(UNIT_ROOMS_MAX);

const unitNumber = storableText(UNIT_NUMBER_MAX_LENGTH);

export const unitRequest = z
  .object({
    number: unitNumber,
    floorPlan: z.preprocess(absentOrBlankAsNull, storableText(UNIT_FLOOR_PLAN_MAX_LENGTH).nullable()),
    beds: rooms,
    baths: rooms,
  })
  .strict();
export type UnitRequest = { number: string; floorPlan?: string | null; beds: number; baths: number };

export const missingUnit = z
  .object({
    property: recordId.toLowerCase(),
    number: unitNumber,
    beds: rooms,
    baths: rooms,
  })
  .strict();
export type MissingUnit = z.infer<typeof missingUnit>;

export const missingUnitsRequest = z.object({ units: z.array(missingUnit).min(1).max(MISSING_UNITS_MAX) }).strict();
export type MissingUnitsRequest = z.infer<typeof missingUnitsRequest>;

export const missingUnits = z.object({ items: z.array(unit) });
export type MissingUnits = z.infer<typeof missingUnits>;
